import React from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PricingCardProps {
  title: string;
  description: string;
  price: string;
  period: string;
  features: string[];
  buttonText: string;
  buttonVariant: 'primary' | 'secondary';
  popular: boolean;
  delay: number;
}

const PricingCard: React.FC<PricingCardProps> = ({
  title,
  description,
  price,
  period,
  features,
  buttonText,
  buttonVariant,
  popular,
  delay
}) => {
  const { t } = useTranslation();
  const getGradient = (index: number) => {
    const gradients = [
      'from-blue-500 to-indigo-600',
      'from-purple-500 to-pink-600',
      'from-emerald-500 to-teal-600',
      'from-orange-500 to-red-600'
    ];
    return gradients[Math.floor(delay / 100)] || gradients[0];
  };

  const handleScroll = () => {
    const uploadSection = document.querySelector('#authentication-section');
    if (uploadSection) {
      uploadSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div
      className={`group relative bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border transition-all duration-500 hover:transform hover:-translate-y-3 hover:shadow-2xl animate-fade-in flex flex-col h-full ${
        popular 
          ? 'border-indigo-500/50 shadow-2xl scale-105 bg-gradient-to-br from-indigo-500/10 to-purple-500/10' 
          : 'border-slate-700/50 hover:border-indigo-500/50'
      }`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Background gradient effect */}
      <div className={`absolute inset-0 bg-gradient-to-br ${getGradient(delay)} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`}></div>
      
      {popular && (
        <div className="relative text-center mb-4">
          <span className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg animate-pulse">
            {t('landing.pricing.most_popular')}
          </span>
        </div>
      )}
      
      <div className="relative text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-indigo-400 group-hover:to-purple-400 group-hover:bg-clip-text transition-all duration-300">
          {title}
        </h3>
        <p className="text-slate-300 text-sm mb-4 group-hover:text-slate-200 transition-colors duration-300">
          {description}
        </p>
        <div className={`text-4xl font-bold bg-gradient-to-r ${getGradient(delay)} bg-clip-text text-transparent mb-1 group-hover:scale-110 transition-transform duration-300`}>
          {price}
        </div>
        <div className="text-slate-400 text-sm">
          {period}
        </div>
      </div>
      
      <ul className="relative space-y-3 mb-6 flex-1">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center text-sm">
            <div className={`w-5 h-5 bg-gradient-to-r ${getGradient(delay)} rounded-full flex items-center justify-center mr-3 flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
              <Check className="w-3 h-3 text-white" />
            </div>
            <span className="text-slate-300 group-hover:text-slate-200 transition-colors duration-300">
              {feature}
            </span>
          </li>
        ))}
      </ul>
      
      <div className="mt-auto pt-4">
        <button
          className={`relative w-full py-3 px-4 rounded-xl font-semibold transition-all duration-300 group-hover:scale-105 shadow-lg hover:shadow-xl ${
            buttonVariant === 'primary'
              ? `bg-gradient-to-r ${getGradient(delay)} text-white hover:shadow-indigo-500/25`
              : 'bg-slate-700/50 text-slate-300 border border-slate-600/50 hover:bg-slate-600/50 hover:text-white hover:border-indigo-500/50'
          }`}
          onClick={handleScroll}
        >
          {buttonText}
        </button>
      </div>
      
      {/* Animated corner accent */}
      <div className={`absolute top-0 right-0 w-0 h-0 border-l-[20px] border-l-transparent border-t-[20px] border-t-gradient-to-r ${getGradient(delay)} rounded-tr-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>
    </div>
  );
};

export default PricingCard;